/**
 * AIImproveButton — small "✨ Improve with AI" pill button that sits under a
 * <textarea> / <input>. Calls the backend Emergent LLM key to rewrite the
 * text and swaps the value in place. Uses a shallow toast for errors.
 *
 * Usage:
 *   <AIImproveButton
 *     value={text}
 *     onImproved={(newText) => setText(newText)}
 *     kind="bio"        // bio | job_description | skill_description | achievement_description | general
 *   />
 */
import { useState } from "react";
import { Sparkles, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

export default function AIImproveButton({ value, onImproved, kind = "general", disabled, testId }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const run = async () => {
    const text = (value || "").trim();
    if (text.length < 10) {
      toast.error("Write a little more first (at least 10 characters).");
      return;
    }
    setBusy(true); setDone(false);
    try {
      const { data } = await http.post("/ai/improve-text", { text, kind });
      if (data?.text) {
        onImproved(data.text);
        setDone(true);
        setTimeout(() => setDone(false), 2000);
      } else {
        toast.error("No improvement returned.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI unavailable. Try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || disabled}
      data-testid={testId || "ai-improve-btn"}
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50"
      style={{
        borderColor: done ? "#10b981" : "var(--wofy-primary)",
        color: done ? "#10b981" : "var(--wofy-primary)",
        background: "transparent",
      }}
      title="Improve this text with AI"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : done ? <Check className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
      {busy ? "Improving…" : done ? "Improved" : "✨ Improve with AI"}
    </button>
  );
}
